/**
 * Droits de l'ecran de vente (plan 1.2 : pos_caisse_employee_right, relus par
 * le poste avec le NIP et la liste des employes ; store/employees.js
 * normalizeRights).
 *
 *   - Rabais : seulement avec can_discount. Plafond max_discount_pct (en %,
 *     deux decimales ; null = sans limite, comme la page ERP « Droits »).
 *     Au-dela du plafond : NIP d'un responsable (is_manager), verifie hors
 *     ligne par le verificateur PBKDF2 du poste.
 *   - Prix modifie : toujours avec le NIP d'un employe qui a
 *     can_override_price ou is_manager (le caissier lui-meme s'il a le droit).
 */
import { TYPES_RABAIS } from './contrat';
import { montantDuRabais } from './panier';

export const DECISIONS_RABAIS = Object.freeze({
    OK: 'OK',
    SANS_DROIT: 'SANS_DROIT',
    INVALIDE: 'INVALIDE',
    RESPONSABLE: 'RESPONSABLE',
});

export const MESSAGES_RABAIS = Object.freeze({
    SANS_DROIT: 'Vous n\'avez pas le droit de faire un rabais. Appelez le responsable.',
    RESPONSABLE: 'Rabais au-delà de votre limite : NIP du responsable',
    TROP_GRAND: 'Le rabais dépasse le montant. Entrez un rabais plus petit.',
    NUL: 'Entrez un rabais plus grand que 0.',
    RIEN: 'Ajoutez un article avant de faire un rabais.',
});

/** Plafond en points de base (1250 = 12,5 %), ou null sans limite. */
export function plafondPointsDeBase(droits) {
    const pct = droits ? droits.max_discount_pct : null;
    if (pct === null || pct === undefined || !Number.isFinite(Number(pct))) return null;
    return Math.max(0, Math.round(Number(pct) * 100));
}

/**
 * Decide d'un rabais demande.
 * @param {object} options
 * @param {object|null} options.droits droits de l'employe connecte
 * @param {'POURCENT'|'MONTANT'} options.type
 * @param {number} options.valeur points de base ou cents
 * @param {number} options.brutCents montant brut vise (ligne ou vente)
 * @returns {{ decision: string, montant_cents: number, message: string|null }}
 */
export function evaluerRabais({ droits, type, valeur, brutCents }) {
    if (!droits || droits.can_discount !== true) {
        return { decision: DECISIONS_RABAIS.SANS_DROIT, montant_cents: 0, message: MESSAGES_RABAIS.SANS_DROIT };
    }
    if (!Number.isSafeInteger(brutCents) || brutCents <= 0) {
        return { decision: DECISIONS_RABAIS.INVALIDE, montant_cents: 0, message: MESSAGES_RABAIS.RIEN };
    }
    if (!Number.isSafeInteger(valeur) || valeur <= 0 || !Object.values(TYPES_RABAIS).includes(type)) {
        return { decision: DECISIONS_RABAIS.INVALIDE, montant_cents: 0, message: MESSAGES_RABAIS.NUL };
    }
    if ((type === TYPES_RABAIS.POURCENT && valeur > 10000) || (type === TYPES_RABAIS.MONTANT && valeur > brutCents)) {
        return { decision: DECISIONS_RABAIS.INVALIDE, montant_cents: 0, message: MESSAGES_RABAIS.TROP_GRAND };
    }
    const montant = montantDuRabais({ type, valeur }, brutCents);
    if (montant <= 0) return { decision: DECISIONS_RABAIS.INVALIDE, montant_cents: 0, message: MESSAGES_RABAIS.NUL };
    const plafond = plafondPointsDeBase(droits);
    const depasse = plafond !== null && (type === TYPES_RABAIS.POURCENT
        ? valeur > plafond
        : BigInt(montant) * BigInt(10000) > BigInt(plafond) * BigInt(brutCents));
    if (depasse) return { decision: DECISIONS_RABAIS.RESPONSABLE, montant_cents: montant, message: MESSAGES_RABAIS.RESPONSABLE };
    return { decision: DECISIONS_RABAIS.OK, montant_cents: montant, message: null };
}

/** Peut faire un rabais (bouton « Rabais » visible). */
export function peutFaireRabais(droits) {
    return Boolean(droits && droits.can_discount === true);
}

/** Ce NIP peut-il autoriser un rabais au-dela du plafond ? */
export function peutAutoriserRabais(droits) {
    return Boolean(droits && droits.is_manager === true);
}

/** Ce NIP peut-il modifier un prix ? */
export function peutModifierPrix(droits) {
    return Boolean(droits && (droits.can_override_price === true || droits.is_manager === true));
}
